import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  ListWrapper,
  FiltersDropDownWrapper,
  ContentWrapper,
} from "components/Shared";
import { ListSort, listSortDefault, ListSortQuery } from "components/ListSort";
import { ListFilter, ListFilterQuery } from "components/ListFilter";
import TabNavigation from "components/TabNavigation";
import PageQuery from "components/PageQuery";
import NoAppointment from "components/NoAppointment";
import { useRouter } from "next/router";
import CustomerAppointmentCard from "components/CustomerAppointmentCard";
import CustomerAppointmentModal from "components/CustomerAppointmentModal";
import Alert from "components/Alert";
import Button from "components/Button";
import styled from "styled-components";
import { isEmpty } from "lodash";
import Link from "next/link";
import Pagination from "components/Pagination";
import replacePageSizeAndCurrentPage from "lib/query-pagination";

const AlertWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1rem;
  padding: 0 1rem;
  width: 100%;
`;

const UnavailableAlert = styled(Alert)`
  margin: 0 auto;
  max-width: 64rem;
`;

const UnavailableAlertContainer = styled.div`
  align-items: center;
  display: grid;
  grid-gap: 1.25rem;

  ${({ theme }) => theme.mq.md`
    grid-template-columns: 1fr 146px;
  `}
`;

const UnavailableAlertText = styled.h6`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-size: 1rem;
  line-height: 1.125rem;
  margin-bottom: 0;
  max-width: 500px;
`;

const UnavailableAlertTitle = styled.p`
  color: ${({ theme }) => theme.themeColors.primary};
  font-size: 1rem;
  line-height: 18px;
  margin: 0 0 4px 0;
`;

const PaginationContainer = styled.div`
  padding: 1rem;
  position: relative;
`;

const UnavailableTextContainer = styled.div``;

const listFilters = [
  { label: "All", value: "all" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const ApptContainer = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;
`;

const PendingApptSubheader = styled.h3`
  color: ${({ theme }) => theme.themeColors.goodfriendDark};
  font-weight: 600;
  line-height: 1.1875rem;
  letter-spacing: -0.025rem;
`;

export default function AppointmentCustomerSection({ isList }) {
  const { query, push, asPath } = useRouter();
  const filterIdentifier = query.f;
  const sortIdentifier = query.s;

  const [listFilterSelected, setListFilterSelected] = useState({
    label: filterIdentifier || listFilters[0].label,
    value: filterIdentifier || listFilters[0].value,
  });

  const [listSortSelected, setListSortSelected] = useState({
    label: sortIdentifier || listSortDefault[0].label,
    value: sortIdentifier || listSortDefault[0].value,
  });

  const [pageSize, setPageSize] = useState(query.pageSize || 4);
  const currentPage = query.currentPage || 1;

  const queryObj = {
    currentPage,
    ...(pageSize && { pageSize }),
  };

  const renderApptCardList = ({
    apptCardClickHandler,
    appointments = [],
    isApptListPage,
    setAppointmentData,
    form,
    handleSubmit,
    reload,
  }) => {
    const reschedulingAppts = appointments?.filter(
      (appt) => appt?.attributes?.status === "rescheduling"
    );
    const pendingProviderAppts = appointments?.filter(
      (appt) =>
        appt?.attributes?.status === "pending" &&
        (!appt?.attributes?.requestedBy ||
          appt?.attributes?.requestedBy === "customer_request")
    );
    const pendingCustomerAppts = appointments?.filter(
      (appt) =>
        appt?.attributes?.status === "pending" &&
        appt?.attributes?.requestedBy === "provider_request"
    );

    if (isApptListPage && !isEmpty(appointments)) {
      return (
        <ApptContainer>
          {appointments.map((appointment, index) => (
            <CustomerAppointmentCard
              key={`card-${appointment.id}`}
              appointment={appointment}
              index={index}
              form={form}
              handleSubmit={handleSubmit}
              reload={reload}
              setAppointmentData={setAppointmentData}
              apptCardClickHandler={apptCardClickHandler}
            />
          ))}
        </ApptContainer>
      );
    }

    if (!isApptListPage) {
      return (
        <>
          {!isEmpty(reschedulingAppts) && (
            <ApptContainer>
              <PendingApptSubheader>
                Please select a new time
              </PendingApptSubheader>
              {reschedulingAppts.map((appointment) => (
                <CustomerAppointmentCard
                  key={`card-${appointment.id}`}
                  appointment={appointment}
                  index={appointments.findIndex((a) => a.id === appointment.id)}
                  form={form}
                  handleSubmit={handleSubmit}
                  reload={reload}
                  setAppointmentData={setAppointmentData}
                  apptCardClickHandler={apptCardClickHandler}
                />
              ))}
            </ApptContainer>
          )}

          {!isEmpty(pendingCustomerAppts) && (
            <ApptContainer>
              <PendingApptSubheader>
                Pending Customer Confirmation
              </PendingApptSubheader>
              {pendingCustomerAppts.map((appointment) => (
                <CustomerAppointmentCard
                  key={`card-${appointment.id}`}
                  appointment={appointment}
                  index={appointments.findIndex((a) => a.id === appointment.id)}
                  form={form}
                  handleSubmit={handleSubmit}
                  reload={reload}
                  setAppointmentData={setAppointmentData}
                  apptCardClickHandler={apptCardClickHandler}
                />
              ))}
            </ApptContainer>
          )}

          {!isEmpty(pendingProviderAppts) && (
            <ApptContainer>
              <PendingApptSubheader>
                Pending Provider Confirmation
              </PendingApptSubheader>
              {pendingProviderAppts.map((appointment) => (
                <CustomerAppointmentCard
                  key={`card-${appointment.id}`}
                  appointment={appointment}
                  index={appointments.findIndex((a) => a.id === appointment.id)}
                  form={form}
                  handleSubmit={handleSubmit}
                  reload={reload}
                  setAppointmentData={setAppointmentData}
                  apptCardClickHandler={apptCardClickHandler}
                />
              ))}
            </ApptContainer>
          )}
          {isEmpty(reschedulingAppts) &&
            isEmpty(pendingCustomerAppts) &&
            isEmpty(pendingProviderAppts) && <NoAppointment />}
        </>
      );
    }

    return <NoAppointment />;
  };

  return (
    <>
      <PageQuery
        userType="customer"
        query={{
          appointments: {
            fields: [
              "availabilityChannelId",
              "cancelledAt",
              "cancelledBy",
              "cancellationFee",
              "cancellationNotes",
              "cancellationTimeframe",
              "confirmedAt",
              "createdAt",
              "customerFeeInt",
              "customerNotes",
              "customerPhoneNumber",
              "customerUserPaymentMethod",
              "customerUserId",
              "discountInt",
              "endDatetime",
              "formattedAddress",
              "location",
              "notes",
              "paymentStatus",
              "promoDiscount",
              "requestChangeAt",
              "requestedAt",
              "requestedBy",
              "rescheduleLink",
              "paymentLink",
              "servicesAmountInt",
              "skipPayments",
              "startDatetime",
              "status",
              "stripeCharge",
              "subtotal",
              "tipInt",
              "tippingCompleted",
              "totalChargeInt",
              "totalCustomerAmountInt",
              "vendorBusinessId",
            ],
            paginate: { number: queryObj.currentPage, size: queryObj.pageSize },
            readOnly: [
              "rescheduleLink",
              "paymentLink",
              "paymentStatus",
              "promoDiscount",
              "servicesAmountInt",
              "skipPayments",
              "totalChargeInt",
              "cancellationFee",
              "customerFeeInt",
              "stripeCharge",
              "subtotal",
              "totalCustomerAmountInt",
              "customerUserPaymentMethod",
            ],
            include: {
              vendorBusiness: {
                readOnly: true,
                id: { fromContext: "appointment.vendorBusinessId" },
                fields: [
                  "id",
                  "businessName",
                  "formattedAddress",
                  "logo",
                  "phone",
                  "website",
                  "contactEmail",
                  "externalLink",
                  "skipNotes",
                  "limitedStatusUpdates",
                  "skipCustomerStatusUpdates",
                ],
              },
              customerUser: {
                readOnly: true,
                id: { fromContext: "appointment.customerUserId" },
                fields: ["name"],
              },
              serviceTypes: {
                readOnly: true,
                id: { fromContext: "appointment.vendorBusinessId" },
                fields: ["name"],
              },
              pet: {
                readOnly: true,
                id: { fromContext: "appointment.petId" },
                fields: ["name", "logo"],
              },
              appointmentLineItems: {
                readOnly: true,
                fields: ["name", "price", "duration"],
                filter: { appointmentId: { fromContext: "appointment.id" } },
              },
              appointmentPromoCodes: {
                readOnly: true,
                fields: ["promoCodeId", "promoCodeName", "appointmentId"],
                filter: { appointmentId: { fromContext: "appointment.id" } },
              },
              employee: {
                readOnly: true,
                fields: ["name", "availabilityChannelId"],
                find: {
                  availabilityChannelId: {
                    fromContext: "appointment.availabilityChannelId",
                  },
                },
              },
            },
            ...ListFilterQuery({
              listFilterSelected,
              isList,
            }),
            ...ListSortQuery(listSortSelected, sortIdentifier),
          },
        }}
      >
        {({ data, form, reload, handleSubmit }) => {
          const [appointmentData, setAppointmentData] = useState(undefined); // eslint-disable-line
          const [showModal, setShowModal] = useState(false); // eslint-disable-line
          const [fieldIndex, setFieldIndex] = useState(0); // eslint-disable-line
          const appointments = data?.appointments?.data;
          const unavailableAppts = appointments?.filter(
            // eslint-disable-next-line comma-dangle
            (appt) => appt?.attributes?.status === "rescheduling"
          );
          const apptCardClickHandler = (appointment, index) => {
            setFieldIndex(index);
            setAppointmentData(appointment);
            setShowModal(!showModal);
          };
          return (
            <>
              <AlertWrapper>
                {!isEmpty(unavailableAppts) && (
                  <UnavailableAlert themeColor="primary">
                    <UnavailableAlertContainer>
                      <UnavailableTextContainer>
                        <UnavailableAlertTitle>
                          You've got requests to select different appointment
                          times
                        </UnavailableAlertTitle>
                        <UnavailableAlertText>
                          We’re sorry to tell you that the requested date/time
                          for an appointment is unavailable. Click a button if
                          you want to make an appointment with the new schedule.
                        </UnavailableAlertText>
                      </UnavailableTextContainer>
                      <Link href="/customer/appointments/my-requests" passHref>
                        <Button
                          colorType="primary"
                          type="button"
                          role="button"
                          outlineType="outline"
                        >
                          View requests
                        </Button>
                      </Link>
                    </UnavailableAlertContainer>
                  </UnavailableAlert>
                )}
              </AlertWrapper>
              <TabNavigation
                isQuery
                pageList={[
                  {
                    link: { pathname: "/customer/appointments" },
                    title: "Appointments List",
                  },
                  {
                    link: { pathname: "/customer/appointments/my-requests" },
                    title: "My Requests",
                  },
                ]}
              />
              <ContentWrapper>
                {isList ? (
                  <ListFilter
                    listFilters={listFilters}
                    filterIdentifier={filterIdentifier}
                    listFilterSelected={listFilterSelected}
                    setListFilterSelected={setListFilterSelected}
                    sortIdentifier={sortIdentifier}
                    listSortSelected={listSortSelected}
                    setListSortSelected={setListSortSelected}
                  />
                ) : (
                  <FiltersDropDownWrapper>
                    <ListSort
                      listSortSelected={listSortSelected}
                      setListSortSelected={setListSortSelected}
                    />
                  </FiltersDropDownWrapper>
                )}
                <ListWrapper>
                  {renderApptCardList({
                    apptCardClickHandler,
                    appointments: data?.appointments?.data,
                    isApptListPage: isList,
                    setAppointmentData,
                    form,
                    handleSubmit,
                    reload,
                  })}
                </ListWrapper>
                <PaginationContainer>
                  <Pagination
                    setPageSize={(numberPageItems) => {
                      setPageSize(numberPageItems);
                      push({
                        query: {
                          ...query,
                          currentPage: 1,
                          pageSize: numberPageItems,
                        },
                      });
                    }}
                    handlePageSelection={(page) => {
                      const newUrl = replacePageSizeAndCurrentPage(
                        asPath,
                        pageSize,
                        page
                      );
                      window.history.pushState(
                        { ...window.history.state, as: newUrl, url: newUrl },
                        "",
                        newUrl
                      );
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    {...data.appointments?.pagination}
                  />
                </PaginationContainer>
                {showModal && (
                  <CustomerAppointmentModal
                    data={appointmentData}
                    close={() => setShowModal(false)}
                    padding="2rem"
                    showModal={showModal}
                    form={form}
                    fieldIndex={fieldIndex}
                    reload={reload}
                    handleSubmit={handleSubmit}
                    setShowModal={setShowModal}
                  />
                )}
              </ContentWrapper>
            </>
          );
        }}
      </PageQuery>
    </>
  );
}

AppointmentCustomerSection.propTypes = {
  isList: PropTypes.bool,
};

AppointmentCustomerSection.defaultProps = {
  isList: true,
};
